<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer/>
      <div class="d-flex justify-end align-center d-print-none">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca subrecetas por nombre"
          solo
          hide-details
          rounded
          flat
          dense />
      </div>
    </div>

    <v-flex xs12>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="table.headers"
        :items="recipesList.models"
        :options="table.options"
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 d-print-table fixed-header">
        <template v-slot:item.name="{ item }">
          <a
            href="" @click.prevent="openUpdateIngredientsDialog(item.$)">
            {{ item.name }}
          </a>
        </template>
        <template v-slot:item.restaurant="{ item }">
          {{ item.restaurant ? item.restaurant.name : '' }}
        </template>
        <template v-slot:item.production="{ item }">
          <div class="text-right">
            {{ item.production | toFixNumber(2) |numberWithComma }}
            {{ item.unitStock.code }}
          </div>
        </template>
        <template v-slot:item.cost="{ item }">
          <div class="text-right">
            ${{ item.cost | toFix2 |numberWithComma }}
          </div>
        </template>
        <template v-slot:item.unitCost="{ item }">
          <div class="text-right">
            <span v-if="item.unitCost">
              ${{ item.unitCost | toFix2 | numberWithComma }} / {{ item.unitStock.code }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <template>
            <v-chip
              v-if="item.status === 'attached'"
              class="black--text success">
              {{ convertStatus(item.status)['text'] }}
            </v-chip>
            <v-chip
              v-else
              class="black--text">
              {{ convertStatus(item.status)['text'] }}
            </v-chip>
          </template>
        </template>
      </v-data-table>
    </v-flex>

    <ProductionDialog
      :recipe="recipe"
      :ingredientDialog="ingredientDialog"
      @closeIngredientDialog="closeIngredientDialog"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ProductionDialog from '@/screens/subrecipes/ProductionDetail'
import { cloneDeep } from 'lodash'
import Recipe from '@/models/Recipe'
import RecipesList from '@/models/collections/RecipesList'

export default {
  name: 'Production',
  components: {
    ProductionDialog
  },
  data () {
    let recipesList = new RecipesList()
    let recipeModel = new Recipe({}, recipesList)

    return {
      recipesList,
      recipeModel,
      ingredientDialog: false,
      overlay: false,
      section: this.$i18n.t('production.section'),
      recipe: {
        uuid: null
      },
      table: {
        title: this.$i18n.t('production.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('production.headers.name'), value: 'name' },
          { text: this.$i18n.t('production.headers.production'), value: 'production', sortable: false },
          { text: this.$i18n.t('production.headers.cost'), value: 'cost', sortable: false },
          { text: this.$i18n.t('production.headers.unitCost'), value: 'unitCost', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      status: [
        { text: 'En elaboración', value: 'in-progress' },
        { text: 'Listado como producto', value: 'attached' },
        { text: 'No adjuntado', value: 'unattached' },
        { text: 'Archivado', value: 'archived' }
      ],
      search: ''
    }
  },
  watch: {
    'search': async function (val) {
      if (val.length >= 3) {
        const params = {
          area: this.$store.state.area.id,
          limit: this.table.options.itemsPerPage,
          skip: 0,
          status: 'attached',
          sort: 'name',
          name: `/${val}/i`,
          subrecipe: true
        }
        try {
          this.load(params)
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      } else {
        this.load()
      }
    }
  },
  methods: {
    closeIngredientDialog () {
      this.ingredientDialog = false
      this.load()
    },
    openUpdateIngredientsDialog: async function (recipe) {
      this.recipe = cloneDeep(recipe)
      this.ingredientDialog = true
    },
    convertStatus (key) {
      return this.status.find(stat => stat.value === key) || ''
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      await this.load({
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`,
        status: 'attached',
        area: this.$store.state.area.id,
        subrecipe: true
      })
    },
    async load (params = null) {
      params = params || {
        area: this.$store.state.area.id,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        status: 'attached',
        sort: 'name',
        subrecipe: true
      }
      this.table.loading = true
      try {
        const { response } = await this.recipeModel.fetch({ params })
        this.recipesList.replace([response.data.data])
        this.table.total = response.data.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    submit: async function () {
      if (this.$refs.recipeForm.validate()) {
        const request = this.form.data.uuid == null
          ? this.create()
          : this.update()
        this.overlay = true
        try {
          await request
          this.load()
          this.closeRecipesFormDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    fetch: async function (entity, params) {
      return this.$api.get(`/v1/${entity}`, params)
    },
    getSubrecipeColor (isSubrecipe) {
      return isSubrecipe ? 'teal darken-4' : 'teal lighten-5'
    },
    getSubrecipeTextColor (isSubrecipe) {
      return isSubrecipe ? 'teal lighten-5' : 'teal darken-4'
    }
  }
}
</script>

<style lang="css" scoped>
</style>
